// apps/office/src/pages/office/InteriorInvoiceForm.jsx
import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import {
  ArrowLeft, Loader2, Send, FileText, AlertCircle, Receipt,
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import pb from '../../lib/pocketbaseClient';
import { useAuth } from '../../contexts/AuthContext';

const rs = (v) => 'Rs. ' + new Intl.NumberFormat('en-IN', { maximumFractionDigits: 0 }).format(Number(v) || 0);
const pArr = (v) => {
  if (!v) return [];
  if (Array.isArray(v)) return v;
  if (typeof v === 'string') { try { const p = JSON.parse(v); return Array.isArray(p) ? p : []; } catch (e) { return []; } }
  return [];
};

const InteriorInvoiceForm = () => {
  const { quotationId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const [submitting, setSubmitting] = useState(false);

  /* ─── Load quotation + project + client ─── */
  const { data: quotation, isLoading } = useQuery({
    queryKey: ['interior-invoice-form-quotation', quotationId],
    queryFn: () => pb.collection('interior_quotations').getOne(quotationId, { expand: 'projectId' }),
    enabled: !!quotationId,
  });

  const { data: project } = useQuery({
    queryKey: ['interior-invoice-form-project', quotation?.projectId],
    queryFn: () => pb.collection('interior_projects').getOne(quotation.projectId),
    enabled: !!quotation?.projectId,
  });

  const { data: client } = useQuery({
    queryKey: ['interior-invoice-form-client', project?.clientId],
    queryFn: () => pb.collection('leads').getOne(project.clientId),
    enabled: !!project?.clientId,
  });

  /* ─── Check existing invoice for this quotation ─── */
  const { data: existingInvoice } = useQuery({
    queryKey: ['interior-invoice-existing', quotationId],
    queryFn: async () => {
      try {
        return await pb.collection('invoices').getFirstListItem(`interiorQuotationId = "${quotationId}"`, { sort: '-created' });
      } catch (e) { return null; }
    },
    enabled: !!quotationId,
  });

  /* ─── Calculations ─── */
  const items = pArr(quotation?.items);
  const profitPct = Number(quotation?.profitMargin) || 20;
  const profitFactor = 1 + profitPct / 100;
  const subTotal = items.reduce((sum, b) => {
    const prods = pArr(b.products || b.items);
    return sum + prods.reduce((s, p) => s + ((Number(p.amount) || 0) * profitFactor), 0);
  }, 0);
  const discType = quotation?.discountType || 'percentage';
  const discValue = Number(quotation?.discountValue) || 0;
  const disc = discType === 'percentage' ? subTotal * discValue / 100 : discValue;
  const initialQuote = subTotal - disc;
  const cgstEnabled = !!quotation?.cgstEnabled;
  const cgstRate = Number(quotation?.cgstRate) || 9;
  const cgstAmt = cgstEnabled ? initialQuote * cgstRate / 100 : 0;
  const sgstEnabled = !!quotation?.sgstEnabled;
  const sgstRate = Number(quotation?.sgstRate) || 9;
  const sgstAmt = sgstEnabled ? initialQuote * sgstRate / 100 : 0;
  const finalTotal = initialQuote + cgstAmt + sgstAmt;

  /* ─── Generate invoice number ─── */
  const genInvoiceNumber = async () => {
    try {
      const last = await pb.collection('invoices').getFirstListItem('', { sort: '-invoiceNumber' });
      let n = 99100;
      if (last?.invoiceNumber) {
        const m = last.invoiceNumber.match(/INV-(\d+)/);
        if (m) n = parseInt(m[1], 10);
      }
      return `INV-${n + 1}`;
    } catch { return 'INV-99101'; }
  };

  /* ─── Submit for approval ─── */
  const handleSubmit = async () => {
    if (!quotation) { toast.error('Quotation not loaded'); return; }
    if (existingInvoice) {
      toast.error('An invoice already exists for this quotation');
      return;
    }
    if (!quotation.status || quotation.status !== 'Approved') {
      toast.error('Only approved quotations can create invoice');
      return;
    }

    setSubmitting(true);
    try {
      const invNumber = await genInvoiceNumber();

      const payload = {
        invoiceNumber: invNumber,
        quotationId: null,
        interiorQuotationId: quotationId,
        leadId: project?.clientId || null,
        field: 'Pending Approval',
        source: 'interior',
        invoiceDate: new Date().toISOString().split('T')[0],
        dueDate: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
        discountAmount: disc,
        taxAmount: cgstAmt + sgstAmt,
        totalAmount: finalTotal,
        notes: `Interior invoice for ${quotation.quoteNumber || quotationId}`,
      };

      const record = await pb.collection('invoices').create(payload);

      // Notify all admins
      try {
        const admins = await pb.collection('users').getFullList({
          filter: 'role = "admin" || role = "super_admin" || role = "manager"',
        });
        for (const admin of admins) {
          try {
            await pb.collection('notifications').create({
              userId: admin.id,
              type: 'order_placed',
              message: `${user?.name || 'User'} sent interior invoice ${invNumber} for approval.`,
              isRead: false,
            });
          } catch (e) {}
        }
      } catch (e) {}

      toast.success(`✅ ${invNumber} submitted for approval`);
      queryClient.invalidateQueries(['invoices']);
      queryClient.invalidateQueries(['interior-invoice-existing', quotationId]);
      navigate('/office/invoices');
    } catch (err) {
      let msg = err.message || 'Failed to submit';
      if (err.originalError?.data?.message) msg = err.originalError.data.message;
      toast.error('❌ ' + msg);
    } finally {
      setSubmitting(false);
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-96">
        <Loader2 className="w-8 h-8 animate-spin text-emerald-500" />
      </div>
    );
  }

  if (!quotation) {
    return (
      <div className="text-center py-16">
        <p className="text-red-500 font-medium">Quotation not found</p>
        <Button variant="outline" className="mt-4" onClick={() => navigate(-1)}>Back</Button>
      </div>
    );
  }

  /* ─── Already submitted ─── */
  if (existingInvoice) {
    const f = existingInvoice.field || 'Draft';
    return (
      <div className="max-w-3xl mx-auto space-y-6 pb-20 p-4">
        <div className="flex items-center gap-3">
          <Button variant="outline" size="icon" onClick={() => navigate(-1)}>
            <ArrowLeft className="w-4 h-4" />
          </Button>
          <h1 className="text-2xl font-bold">Invoice Already Exists</h1>
        </div>

        <Card className="border-2 border-amber-200 bg-amber-50">
          <CardContent className="p-6 flex items-start gap-3">
            <AlertCircle className="w-6 h-6 text-amber-600 mt-0.5 shrink-0" />
            <div className="flex-1">
              <p className="font-semibold text-amber-900 mb-1">
                Invoice {existingInvoice.invoiceNumber} already created
              </p>
              <p className="text-sm text-amber-800 mb-3">
                Status: <Badge className={
                  f === 'Approved' ? 'bg-green-100 text-green-700' :
                  f === 'Pending Approval' ? 'bg-yellow-100 text-yellow-700' :
                  f === 'Paid' ? 'bg-emerald-100 text-emerald-700' :
                  'bg-gray-100 text-gray-700'
                }>{f}</Badge>
              </p>
              <Button onClick={() => navigate('/office/invoices')} className="gap-2 bg-amber-600 hover:bg-amber-700 text-white">
                <FileText className="w-4 h-4" /> View in Invoices
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  /* ─── Preview + Submit ─── */
  return (
    <div className="max-w-4xl mx-auto space-y-6 pb-20 p-4">
      {/* Header */}
      <div className="flex items-center gap-3">
        <Button variant="outline" size="icon" onClick={() => navigate(-1)}>
          <ArrowLeft className="w-4 h-4" />
        </Button>
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <Receipt className="w-6 h-6 text-emerald-600" /> Create Interior Invoice
          </h1>
          <p className="text-sm text-slate-500">
            {quotation.quoteNumber} · {client?.name || 'Client'} · {project?.propertyName || 'Project'}
          </p>
        </div>
      </div>

      {/* Info banner */}
      <Card className="border-2 border-blue-200 bg-blue-50">
        <CardContent className="p-4 flex items-start gap-3">
          <AlertCircle className="w-5 h-5 text-blue-600 mt-0.5 shrink-0" />
          <div className="text-sm text-blue-900">
            <p className="font-semibold mb-1">Approval Required</p>
            <p>Submitting this invoice will send it to admin for approval. Once approved, the invoice PDF will be generated.</p>
          </div>
        </CardContent>
      </Card>

      {/* Summary Card */}
      <Card>
        <CardHeader className="bg-slate-50 border-b">
          <CardTitle className="text-base">Invoice Summary</CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          <div className="divide-y">
            <div className="flex justify-between px-6 py-3">
              <span className="text-slate-600">Rooms</span>
              <span className="font-semibold">{items.length}</span>
            </div>
            <div className="flex justify-between px-6 py-3">
              <span className="text-slate-600">Sub Total</span>
              <span className="font-semibold">{rs(subTotal)}</span>
            </div>
            {disc > 0 && (
              <div className="flex justify-between px-6 py-3">
                <span className="text-slate-600">
                  Discount {discType === 'percentage' ? `(${discValue}%)` : ''}
                </span>
                <span className="font-semibold text-red-600">- {rs(disc)}</span>
              </div>
            )}
            <div className="flex justify-between px-6 py-3 bg-slate-50">
              <span className="font-semibold">Initial Quote Value</span>
              <span className="font-semibold">{rs(initialQuote)}</span>
            </div>
            {cgstAmt > 0 && (
              <div className="flex justify-between px-6 py-3">
                <span className="text-slate-600 pl-10">CGST @ {cgstRate}%</span>
                <span className="font-semibold">+ {rs(cgstAmt)}</span>
              </div>
            )}
            {sgstAmt > 0 && (
              <div className="flex justify-between px-6 py-3">
                <span className="text-slate-600 pl-10">SGST @ {sgstRate}%</span>
                <span className="font-semibold">+ {rs(sgstAmt)}</span>
              </div>
            )}
            <div className="flex justify-between px-6 py-4 bg-amber-50 border-t-2 border-amber-200">
              <span className="font-bold text-amber-900">FINAL QUOTE VALUE</span>
              <span className="font-bold text-lg text-amber-900">{rs(finalTotal)}</span>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Submit */}
      <div className="flex justify-end gap-3">
        <Button variant="outline" onClick={() => navigate(-1)} disabled={submitting}>
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          disabled={submitting || quotation.status !== 'Approved'}
          className="bg-emerald-600 hover:bg-emerald-700 text-white gap-2"
        >
          {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
          Submit for Approval
        </Button>
      </div>
    </div>
  );
};

export default InteriorInvoiceForm;
